<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useTrelloBoardStore } from "src/stores/trello-board-store";
import { FetchTicketResponsePriority, UnitFiltersDTOMatchMode } from "src/api/models";
import type { FetchAssigneeResponse, FetchUnitResponseLite, UnitFiltersDTO } from "src/api/models";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import DatePicker from "primevue/datepicker";
import AutoComplete from "primevue/autocomplete";
import type { AutoCompleteCompleteEvent } from "primevue/autocomplete";
import { useDebounceFn } from "@vueuse/core";
import { useAssigneeService } from "src/services/assignee-service";
import { useUnitService } from "src/services/unit-service";
import { TicketCreateDialog } from "src/components/ticket-edit";

const emits = defineEmits<{
  "clear-filters": [];
  "open-ticket": [id: string];
}>();

const searchInput = defineModel<string>("searchInput");
const selectedStatusSort = defineModel<string[]>("selectedStatusSort");
const selectedPrioritySort = defineModel<FetchTicketResponsePriority[]>("selectedPrioritySort");
const selectedAssignees = defineModel<string[]>("selectedAssignees");
const selectedUnits = defineModel<string[]>("selectedUnits");
const startDate = defineModel<Date>("startDate");
const ticketContainerWidth = defineModel<number>("ticketContainerWidth");
const isWeekendVisible = defineModel<boolean>("isWeekendVisible");
const singleLaneScroll = defineModel<boolean>("singleLaneScroll");

const isCreateTicketDialogVisible = ref(false);
const store = useTrelloBoardStore();
const { searchAssignees } = useAssigneeService();
const { fetchUnits } = useUnitService();

// Assignee autocomplete state
const PAGE_SIZE = 25;
const selectedAssigneeObject = ref<FetchAssigneeResponse | null>(null);
const assigneeSuggestions = ref<FetchAssigneeResponse[]>([]);
const assigneeLoading = ref(false);
const assigneeTotalSize = ref(0);
const assigneeCurrentQuery = ref("");

const assigneeScrollerOptions = computed(() => ({
  itemSize: 38,
  lazy: true,
  loading: assigneeLoading.value,
  totalSize: assigneeTotalSize.value,
  onLazyLoad: handleAssigneeLazyLoad,
}));

async function handleAssigneeLazyLoad(event: { first: number }) {
  if (assigneeLoading.value || event.first < assigneeSuggestions.value.length) return;
  const page = Math.floor(event.first / PAGE_SIZE) + 1;
  assigneeLoading.value = true;
  try {
    const response = await searchAssignees(assigneeCurrentQuery.value || null, page, PAGE_SIZE);
    const newItems = response?.data ?? [];
    assigneeSuggestions.value = [...assigneeSuggestions.value, ...newItems];
    assigneeTotalSize.value = (response?.total_pages ?? 1) * PAGE_SIZE;
  } finally {
    assigneeLoading.value = false;
  }
}

const debouncedAssigneeComplete = useDebounceFn(async (event: AutoCompleteCompleteEvent) => {
  assigneeCurrentQuery.value = event.query;
  assigneeLoading.value = true;
  try {
    const response = await searchAssignees(event.query || null, 1, PAGE_SIZE);
    assigneeSuggestions.value = response?.data ?? [];
    assigneeTotalSize.value = (response?.total_pages ?? 1) * PAGE_SIZE;
  } catch {
    assigneeSuggestions.value = [];
    assigneeTotalSize.value = 0;
  } finally {
    assigneeLoading.value = false;
  }
}, 300);

function handleAssigneeSelect(event: { value: FetchAssigneeResponse }) {
  selectedAssignees.value = event.value.id ? [event.value.id] : [];
  store.selectedAssigneeName = event.value.name ?? "";
}

// Sync object → filter array (PrimeVue clear button sets v-model to null)
watch(selectedAssigneeObject, (newVal) => {
  if (!newVal) {
    selectedAssignees.value = [];
    store.selectedAssigneeName = "";
  }
});

// Sync filter array → object (external code resets selectedAssignees, e.g. clearFilters)
watch(selectedAssignees, (newVal) => {
  if (!newVal || newVal.length === 0) {
    selectedAssigneeObject.value = null;
  }
}, { deep: true });

// Restore assignee display object from persisted name on mount
if ((selectedAssignees.value?.length ?? 0) > 0 && store.selectedAssigneeName) {
  selectedAssigneeObject.value = { id: selectedAssignees.value![0], name: store.selectedAssigneeName } as FetchAssigneeResponse;
}

// Unit autocomplete state
const selectedUnitObject = ref<FetchUnitResponseLite | null>(null);
const unitSuggestions = ref<FetchUnitResponseLite[]>([]);
const unitLoading = ref(false);
const unitTotalSize = ref(0);
const unitCurrentQuery = ref("");

const unitScrollerOptions = computed(() => ({
  itemSize: 38,
  lazy: true,
  loading: unitLoading.value,
  totalSize: unitTotalSize.value,
  onLazyLoad: handleUnitLazyLoad,
}));

function buildUnitFilters(query: string): UnitFiltersDTO {
  const filters: UnitFiltersDTO = { match_mode: UnitFiltersDTOMatchMode.or };
  if (query) {
    filters.name = query;
    filters.serial_number = query;
    const parsed = parseInt(query, 10);
    if (!isNaN(parsed)) {
      filters.fleet_number = parsed;
    }
  }
  return filters;
}

async function handleUnitLazyLoad(event: { first: number }) {
  if (unitLoading.value || event.first < unitSuggestions.value.length) return;
  const page = Math.floor(event.first / PAGE_SIZE) + 1;
  unitLoading.value = true;
  try {
    const response = await fetchUnits(
      buildUnitFilters(unitCurrentQuery.value),
      { page, limit: PAGE_SIZE },
    );
    const newItems = response?.data ?? [];
    unitSuggestions.value = [...unitSuggestions.value, ...newItems];
    unitTotalSize.value = (response?.total_pages ?? 1) * PAGE_SIZE;
  } finally {
    unitLoading.value = false;
  }
}

const debouncedUnitComplete = useDebounceFn(async (event: AutoCompleteCompleteEvent) => {
  unitCurrentQuery.value = event.query;
  unitLoading.value = true;
  try {
    const response = await fetchUnits(
      buildUnitFilters(event.query),
      { page: 1, limit: PAGE_SIZE },
    );
    unitSuggestions.value = response?.data ?? [];
    unitTotalSize.value = (response?.total_pages ?? 1) * PAGE_SIZE;
  } catch {
    unitSuggestions.value = [];
    unitTotalSize.value = 0;
  } finally {
    unitLoading.value = false;
  }
}, 300);

function formatUnitDropdownLabel(unit: FetchUnitResponseLite): string {
  return [
    unit.fleet_number && `#${unit.fleet_number}`,
    unit.name,
    unit.serial_number && `(${unit.serial_number})`,
  ].filter(Boolean).join(" ");
}

function unitInputLabel(unit: FetchUnitResponseLite): string {
  return String(unit.fleet_number ?? unit.serial_number ?? unit.name ?? "");
}

function handleUnitSelect(event: { value: FetchUnitResponseLite }) {
  selectedUnits.value = event.value.id ? [event.value.id] : [];
  store.selectedUnitName = unitInputLabel(event.value);
}

// Sync object → filter array (PrimeVue clear button sets v-model to null)
watch(selectedUnitObject, (newVal) => {
  if (!newVal) {
    selectedUnits.value = [];
    store.selectedUnitName = "";
  }
});

// Sync filter array → object (external code resets selectedUnits, e.g. clearFilters)
watch(selectedUnits, (newVal) => {
  if (!newVal || newVal.length === 0) {
    selectedUnitObject.value = null;
  }
}, { deep: true });

// Restore unit display object from persisted name on mount
if ((selectedUnits.value?.length ?? 0) > 0 && store.selectedUnitName) {
  selectedUnitObject.value = { id: selectedUnits.value![0], name: store.selectedUnitName } as FetchUnitResponseLite;
}

const statusSortOptions = computed(() => {
  return store.ticketStatuses.map((status) => ({
    label: status.name || status.label || '',
    value: status.name || '',
  }));
});

const prioritySortOptions = Object.values(FetchTicketResponsePriority).map((priority) => ({
  label: priority,
  value: priority,
}));

function clearFilters() {
  emits("clear-filters");
}

function handleCreateTicket() {
  isCreateTicketDialogVisible.value = true;
}

function handleStatusChange(value: string | null) {
  if (value === null) {
    selectedStatusSort.value = [];
  } else {
    selectedStatusSort.value = [value];
  }
}

watch(startDate, () => {
  if (!startDate.value) {
    startDate.value = new Date();
  }
});
</script>

<template>
  <div class="flex items-center justify-between pb-3 bg-surface-card gap-4">
    <div class="flex items-center gap-2">
      <Button
        icon="pi pi-filter-slash"
        severity="secondary"
        text
        size="small"
        @click="clearFilters"
        aria-label="Clear filters"
      />
      <IconField>
        <InputIcon class="pi pi-search text-400" />
        <InputText
          v-model="searchInput"
          placeholder="Search tickets..."
          class="w-[100px] lg:w-[150px] xl:w-[200px]"
          size="small"
        />
      </IconField>

      <div class="flex items-center gap-2">
        <Select
          v-model="selectedStatusSort[0]"
          :options="statusSortOptions"
          option-label="label"
          option-value="value"
          placeholder="Status"
          class="w-[150px]"
          size="small"
          :show-clear="true"
          @update:model-value="handleStatusChange"
        />

        <!-- <MultiSelect
          v-model="selectedPrioritySort"
          :options="prioritySortOptions"
          option-label="label"
          option-value="value"
          placeholder="Priority"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="rounded-full text-sm">
              <i class="pi pi-flag mr-2"></i>
              {{ slotProps.value.length }} priorit{{ slotProps.value.length !== 1 ? "ies" : "y" }}
            </div>
          </template>
        </MultiSelect> -->

          <div class="relative min-w-[140px]">
            <AutoComplete
              v-model="selectedAssigneeObject"
              :suggestions="assigneeSuggestions"
              option-label="name"
              :loading="assigneeLoading"
              placeholder="Assignee"
              :virtual-scroller-options="assigneeScrollerOptions"
              :complete-on-focus="true"
              size="small"
              @complete="debouncedAssigneeComplete"
              @item-select="handleAssigneeSelect"
              show-clear
            />
          </div>

          <div class="relative min-w-[140px]">
            <AutoComplete
              v-model="selectedUnitObject"
              :suggestions="unitSuggestions"
              :option-label="(item: FetchUnitResponseLite) => unitInputLabel(item)"
              :loading="unitLoading"
              placeholder="Unit"
              :virtual-scroller-options="unitScrollerOptions"
              :complete-on-focus="true"
              size="small"
              @complete="debouncedUnitComplete"
              @item-select="handleUnitSelect"
              show-clear
            >
              <template #option="{ option }">
                {{ formatUnitDropdownLabel(option) }}
              </template>
            </AutoComplete>
          </div>

        <DatePicker
          v-model="startDate"
          :show-icon="true"
          placeholder="Start date"
          class="w-[200px]"
          size="small"
          show-button-bar
        >
          <template #dropdownicon="{ clickCallback }">
            <div class="flex gap-1">
              <Button
                icon="pi pi-sync"
                severity="secondary"
                text
                size="small"
                @click.stop="startDate = new Date()"
                tooltip="Go to today"
              />
            </div>
          </template>
        </DatePicker>
      </div>
    </div>

    <div class="flex items-center gap-2">
      <div class="w-px h-6 bg-surface-300 dark:bg-surface-600"></div>
      <div class="flex items-center gap-2">
        <Select
            v-model="ticketContainerWidth"
            :options="[
              { label: 'Compact', value: 175 },
              { label: 'Normal', value: 225 },
              { label: 'Wide', value: 275 },
            ]"
          option-label="label"
          option-value="value"
          placeholder="Column width"
          class="w-[110px]"
          size="small"
          dropdown-icon="pi pi-arrows-h"
        />

        <ToggleButton
          v-model="isWeekendVisible"
          off-icon="pi pi-eye-slash"
          on-icon="pi pi-eye"
          size="small"
          off-label="Show weekends"
          on-label="Hide weekends"
        />

        <ToggleButton
          v-model="singleLaneScroll"
          off-icon="pi pi-arrows-v"
          on-icon="pi pi-bars"
          size="small"
          off-label="Full scroll"
          on-label="Single lane"
        />

        <Button icon="pi pi-plus" severity="secondary" outlined size="small" @click="handleCreateTicket" />
      </div>
    </div>

    <TicketCreateDialog v-model="isCreateTicketDialogVisible" @create-ticket="emits('open-ticket', $event)" />
  </div>
</template>
